import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";import { inr, dateStr } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, RefreshCw, ListTree, AlertTriangle, IndianRupee, LayoutList, GanttChart } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_META = {
  planned:      { cls: "bg-slate-100 text-slate-700 border-slate-200",     label: "Planned" },
  in_progress:  { cls: "bg-sky-100 text-sky-800 border-sky-200",           label: "In progress" },
  done:         { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Done" },
  on_hold:      { cls: "bg-amber-100 text-amber-800 border-amber-200",     label: "On hold" },
};

export default function WBSPanel({ project, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    const r = await api.get(`/wbs/evm/${project.project_id}`);
    setData(r.data);
  };
  useEffect(() => { if (project) load(); }, [project?.project_id]);

  const recompute = async (wbs_id) => {
    try {
      await api.post(`/wbs/${wbs_id}/recompute-actual`);
      toast.success("Actual% recomputed from AC");
      load(); onChanged?.();
    } catch { toast.error("Failed"); }
  };
  const remove = async (wbs_id) => {
    if (!confirm("Delete this milestone? Tagged bills/expenses will be untagged.")) return;
    try { await api.delete(`/wbs/${wbs_id}`); toast.success("Milestone deleted"); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };

  if (!data) return <div className="text-sm text-muted-foreground py-4">Loading WBS…</div>;

  const { wbs = [], rollup, untagged_ac = 0 } = data;
  const rollupCPI = rollup?.cpi ?? 0;
  const rollupSPI = rollup?.spi ?? 0;

  return (
    <div className="space-y-4" data-testid="wbs-panel">
      {/* Rollup summary */}
      <Card className="shadow-none border-primary/20 bg-primary/5">
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
          <RSum label="Milestones" value={rollup?.wbs_count ?? 0} data-testid="wbs-rollup-count" />
          <RSum label="Planned cost" value={inr(rollup?.bac || 0, { compact: true })} data-testid="wbs-rollup-bac" />
          <RSum label="Spent (AC)" value={inr(rollup?.ac || 0, { compact: true })} tone="warn" data-testid="wbs-rollup-ac" />
          <RSum label="Billed" value={inr(rollup?.billed || 0, { compact: true })} data-testid="wbs-rollup-billed" />
          <RSum label="Collected" value={inr(rollup?.collected || 0, { compact: true })} tone="good" data-testid="wbs-rollup-collected" />
          <RSum label={`CPI · SPI`} value={`${rollupCPI.toFixed(2)} · ${rollupSPI.toFixed(2)}`}
            tone={rollupCPI >= 1 && rollupSPI >= 1 ? "good" : rollupCPI >= 0.9 ? "warn" : "bad"} data-testid="wbs-rollup-idx" />
        </CardContent>
      </Card>

      {untagged_ac > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          {inr(untagged_ac, { compact: true })} of expenses aren&apos;t tagged to any milestone yet — open the expense and set a WBS.
        </div>
      )}

      <Card className="shadow-none">
        <CardHeader className="pb-2 flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-sm font-semibold font-display flex items-center gap-2">
              <ListTree className="h-4 w-4 text-primary" /> Milestones · WBS
            </CardTitle>
            <div className="text-[11px] text-muted-foreground mt-0.5">Add, amend, omit or recompute at any time — everything downstream re-aggregates.</div>
          </div>
          <Button size="sm" data-testid="wbs-add-btn" className="h-8 bg-primary hover:bg-primary/90"
            onClick={() => { setEditing(null); setDialogOpen(true); }}>
            <Plus className="h-3 w-3 mr-1" /> Add milestone
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <Tabs defaultValue="table">
            <TabsList className="mb-3">
              <TabsTrigger value="table" data-testid="wbs-view-table"><LayoutList className="h-3.5 w-3.5 mr-1" />Table</TabsTrigger>
              <TabsTrigger value="gantt" data-testid="wbs-view-gantt"><GanttChart className="h-3.5 w-3.5 mr-1" />Gantt</TabsTrigger>
            </TabsList>

            <TabsContent value="gantt" className="mt-0">
              <WBSGantt wbs={wbs} onReforecast={async (id, end) => {
                try {
                  await api.patch(`/wbs/${id}`, { planned_end: end });
                  toast.success("Reforecast saved");
                  load(); onChanged?.();
                } catch { toast.error("Failed"); }
              }} onOpenEdit={(w) => { setEditing(w); setDialogOpen(true); }} />
            </TabsContent>

            <TabsContent value="table" className="mt-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                <th className="py-2 font-medium">Code</th>
                <th className="py-2 font-medium">Name · Status</th>
                <th className="py-2 font-medium">Progress (P / A)</th>
                <th className="py-2 font-medium text-right">BAC</th>
                <th className="py-2 font-medium text-right">Spent (AC)</th>
                <th className="py-2 font-medium text-right">Billed / Collected</th>
                <th className="py-2 font-medium text-right">Vendor paid / out.</th>
                <th className="py-2 font-medium text-center">CPI · SPI</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {wbs.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No milestones yet — add the first phase</td></tr>
              )}
              {wbs.map((w) => {
                const meta = STATUS_META[w.status] || STATUS_META.planned;
                const cpiTone = w.cpi >= 1 ? "text-emerald-700" : w.cpi >= 0.9 ? "text-amber-700" : "text-red-700";
                const spiTone = w.spi >= 1 ? "text-emerald-700" : w.spi >= 0.9 ? "text-amber-700" : "text-red-700";
                return (
                  <tr key={w.wbs_id} className="border-b hover:bg-accent/50" data-testid={`wbs-row-${w.wbs_id}`}>
                    <td className="py-2 font-mono text-xs">{w.code}</td>
                    <td className="py-2 max-w-[240px]">
                      <div className="font-medium truncate">{w.name}</div>
                      <Badge className={`${meta.cls} text-[10px] mt-1`}>{meta.label}</Badge>
                    </td>
                    <td className="py-2 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <Progress value={w.actual_progress_pct} className="h-1.5 flex-1" />
                        <span className="text-[10px] font-mono text-muted-foreground shrink-0">{w.planned_progress_pct}%/{w.actual_progress_pct}%</span>
                      </div>
                    </td>
                    <td className="py-2 cell-num">{inr(w.bac, { compact: true })}</td>
                    <td className="py-2 cell-num text-amber-800">{inr(w.ac, { compact: true })}
                      <div className="text-[10px] text-muted-foreground font-mono">{w.expense_count} bills</div>
                    </td>
                    <td className="py-2 cell-num">
                      <div>{inr(w.billed, { compact: true })}</div>
                      <div className="text-[10px] text-emerald-700 font-mono">{inr(w.collected, { compact: true })} rcvd</div>
                    </td>
                    <td className="py-2 cell-num">
                      <div className="text-emerald-700">{inr(w.vendor_paid, { compact: true })}</div>
                      <div className="text-[10px] text-amber-800 font-mono">{inr(w.vendor_outstanding, { compact: true })} due</div>
                    </td>
                    <td className="py-2 text-center">
                      <div className={`text-xs font-mono font-semibold ${cpiTone}`}>{(w.cpi || 0).toFixed(2)}</div>
                      <div className={`text-[10px] font-mono ${spiTone}`}>{(w.spi || 0).toFixed(2)}</div>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`wbs-recompute-${w.wbs_id}`}
                          onClick={() => recompute(w.wbs_id)} title="Recompute actual% from AC"
                          className="h-7 w-7 p-0"><RefreshCw className="h-3.5 w-3.5 text-sky-700" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`wbs-edit-${w.wbs_id}`}
                          onClick={() => { setEditing(w); setDialogOpen(true); }} title="Amend"
                          className="h-7 w-7 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`wbs-delete-${w.wbs_id}`}
                          onClick={() => remove(w.wbs_id)} title="Omit"
                          className="h-7 w-7 p-0 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <WBSDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        project={project} editing={editing}
        onSaved={() => { setDialogOpen(false); load(); onChanged?.(); }}
      />
    </div>
  );
}

function RSum({ label, value, tone = "muted", ...rest }) {
  const toneCls = tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-800" : tone === "bad" ? "text-red-700" : "";
  return (
    <div {...rest}>
      <div className="text-[10px] uppercase font-mono text-muted-foreground">{label}</div>
      <div className={`font-semibold text-sm ${toneCls}`}>{value}</div>
    </div>
  );
}

function WBSGantt({ wbs, onReforecast, onOpenEdit }) {
  const [dragging, setDragging] = useState(null);
  const [barsWidth, setBarsWidth] = useState(0);
  const barsRef = React.useRef(null);
  React.useEffect(() => {
    const el = barsRef.current;
    if (!el) return;
    const update = () => setBarsWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bounds computed defensively so no hook is called after early return
  const withDates = wbs.filter((w) => w.planned_start && w.planned_end);
  const hasAny = withDates.length > 0;
  const minMs = hasAny ? Math.min(...withDates.map((w) => new Date(w.planned_start).getTime())) : 0;
  const maxMs = hasAny ? Math.max(...withDates.map((w) => new Date(dragging?.wbs_id === w.wbs_id ? dragging.previewEnd : w.planned_end).getTime())) : 0;
  const totalMs = Math.max(1, maxMs - minMs);
  const pad = totalMs * 0.03;
  const rangeStart = minMs - pad;
  const rangeEnd = maxMs + pad;
  const rangeMs = Math.max(1, rangeEnd - rangeStart);

  React.useEffect(() => {
    if (!dragging || barsWidth <= 0) return;
    const onMove = (e) => {
      const deltaX = e.clientX - dragging.startX;
      const deltaMs = (deltaX / barsWidth) * rangeMs;
      const newEnd = new Date(dragging.origEndMs + deltaMs);
      const iso = newEnd.toISOString().slice(0, 10);
      setDragging((d) => d ? { ...d, previewEnd: iso } : d);
    };
    const onUp = () => {
      if (!dragging) return;
      const orig = new Date(dragging.origEndMs).toISOString().slice(0, 10);
      const finalEnd = dragging.previewEnd;
      const id = dragging.wbs_id;
      setDragging(null);
      if (finalEnd && finalEnd !== orig) onReforecast(id, finalEnd);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, barsWidth, rangeMs, onReforecast]);

  if (!hasAny) {
    return <div className="text-sm text-muted-foreground text-center py-8">Add planned_start / planned_end to milestones to see the Gantt.</div>;
  }

  const pct = (ms) => ((ms - rangeStart) / rangeMs) * 100;

  const monthTicks = [];
  const start = new Date(rangeStart);
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor.getTime() <= rangeEnd) {
    monthTicks.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const today = Date.now();

  const beginDrag = (w) => (e) => {
    e.preventDefault();
    setDragging({
      wbs_id: w.wbs_id, startX: e.clientX,
      origEndMs: new Date(w.planned_end).getTime(),
      previewEnd: w.planned_end,
    });
  };

  const statusBar = {
    planned:     "bg-slate-400",
    in_progress: "bg-sky-500",
    done:        "bg-emerald-500",
    on_hold:     "bg-amber-500",
  };

  return (
    <div className="border rounded-md bg-card overflow-hidden" data-testid="wbs-gantt">
      {/* Header row with month ticks */}
      <div className="flex items-center border-b bg-muted/40">
        <div className="w-56 shrink-0 px-3 py-2 text-[10px] uppercase font-mono text-muted-foreground">Milestone</div>
        <div className="relative flex-1 h-8 border-l" ref={barsRef}>
          {monthTicks.map((d, i) => (
            <div key={i} className="absolute top-0 h-full border-l border-muted-foreground/10"
              style={{ left: `${pct(d.getTime())}%` }}>
              <div className="absolute top-1 left-1 text-[9px] text-muted-foreground font-mono whitespace-nowrap">
                {d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" })}
              </div>
            </div>
          ))}
          {today >= rangeStart && today <= rangeEnd && (
            <div className="absolute top-0 h-full border-l-2 border-red-500/70" style={{ left: `${pct(today)}%` }} />
          )}
        </div>
      </div>

      {/* Rows */}
      {wbs.map((w) => {
        const hasDates = w.planned_start && w.planned_end;
        const startMs = hasDates ? new Date(w.planned_start).getTime() : 0;
        const endMs = hasDates ? new Date(dragging?.wbs_id === w.wbs_id ? dragging.previewEnd : w.planned_end).getTime() : 0;
        const leftPct = hasDates ? pct(startMs) : 0;
        const widthPct = hasDates ? Math.max(0.5, pct(endMs) - leftPct) : 0;
        const progressPct = Math.min(100, Math.max(0, Number(w.actual_progress_pct || 0)));
        const barCls = statusBar[w.status] || statusBar.planned;
        const isDragging = dragging?.wbs_id === w.wbs_id;

        return (
          <div key={w.wbs_id} className="flex items-center border-b hover:bg-accent/30 group" data-testid={`gantt-row-${w.wbs_id}`}>
            <button className="w-56 shrink-0 px-3 py-2 text-left text-xs truncate hover:bg-accent"
              onClick={() => onOpenEdit(w)} title="Click to amend">
              <div className="font-mono text-[10px] text-muted-foreground">{w.code}</div>
              <div className="font-medium truncate">{w.name}</div>
            </button>
            <div className="relative flex-1 h-10 border-l">
              {hasDates ? (
                <div
                  className={`absolute top-2 h-6 rounded ${barCls}/30 border border-current text-current transition-shadow ${isDragging ? "shadow-lg ring-2 ring-primary" : ""}`}
                  style={{ left: `${leftPct}%`, width: `${widthPct}%`, borderColor: "" }}
                  data-testid={`gantt-bar-${w.wbs_id}`}
                >
                  {/* Progress overlay */}
                  <div className={`h-full rounded-l ${barCls}`} style={{ width: `${progressPct}%` }} />
                  <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-white mix-blend-difference whitespace-nowrap overflow-hidden">
                    {progressPct.toFixed(0)}% · {new Date(startMs).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} → {new Date(endMs).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </div>
                  {/* Drag grip on right edge */}
                  <div
                    onPointerDown={beginDrag(w)}
                    data-testid={`gantt-grip-${w.wbs_id}`}
                    title="Drag to reforecast end date"
                    className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-black/20 hover:bg-black/40 rounded-r"
                  />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center px-3 text-[10px] text-muted-foreground italic">— no dates —</div>
              )}
            </div>
          </div>
        );
      })}
      <div className="px-3 py-2 text-[10px] text-muted-foreground font-mono border-t bg-muted/20">
        Drag the right edge of any bar to reforecast the end date · Click a milestone name to amend all fields · Red vertical line = today
      </div>
    </div>
  );
}


function WBSDialog({ open, onOpenChange, project, editing, onSaved }) {
  const empty = {
    code: "", name: "", description: "",
    planned_cost: 0, planned_start: project?.start_date || "", planned_end: project?.end_date || "",
    planned_progress_pct: 0, actual_progress_pct: 0, status: "planned", sequence: 0,
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (editing) setForm({
      code: editing.code || "", name: editing.name || "", description: editing.description || "",
      planned_cost: editing.planned_cost || 0,
      planned_start: editing.planned_start || "", planned_end: editing.planned_end || "",
      planned_progress_pct: editing.planned_progress_pct || 0,
      actual_progress_pct: editing.actual_progress_pct || 0,
      status: editing.status || "planned", sequence: editing.sequence || 0,
    });
    else setForm(empty);
  }, [editing, project?.project_id, open]);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code & name required");
    const payload = {
      ...form,
      planned_cost: Number(form.planned_cost || 0),
      planned_progress_pct: Number(form.planned_progress_pct || 0),
      actual_progress_pct: Number(form.actual_progress_pct || 0),
      sequence: Number(form.sequence || 0),
    };
    try {
      if (editing) await api.patch(`/wbs/${editing.wbs_id}`, payload);
      else await api.post("/wbs", { project_id: project.project_id, ...payload });
      toast.success(editing ? "Milestone updated" : "Milestone added");
      onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Amend milestone" : "Add milestone"}</DialogTitle>
          <DialogDescription>
            Split the project into billable, budgetable phases. Every expense, invoice and vendor payment can be tagged to a milestone so EVM &amp; MIS light up at that granularity.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Code (e.g. 1.0, 2.0)"><Input data-testid="wbs-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></F>
          <F label="Sequence"><Input type="number" value={form.sequence} onChange={(e) => setForm({ ...form, sequence: e.target.value })} /></F>
          <F label="Name" full><Input data-testid="wbs-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aluminium Fabrication (YKK)" /></F>
          <F label="Description" full><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
          <F label="Planned cost (₹) — BAC"><Input type="number" data-testid="wbs-bac" value={form.planned_cost} onChange={(e) => setForm({ ...form, planned_cost: e.target.value })} /></F>
          <F label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="wbs-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Planned start"><Input type="date" value={form.planned_start} onChange={(e) => setForm({ ...form, planned_start: e.target.value })} /></F>
          <F label="Planned end"><Input type="date" value={form.planned_end} onChange={(e) => setForm({ ...form, planned_end: e.target.value })} /></F>
          <F label="Planned progress %"><Input type="number" value={form.planned_progress_pct} onChange={(e) => setForm({ ...form, planned_progress_pct: e.target.value })} /></F>
          <F label="Actual progress %"><Input type="number" value={form.actual_progress_pct} onChange={(e) => setForm({ ...form, actual_progress_pct: e.target.value })} /></F>
        </div>
        <DialogFooter>
          <Button data-testid="wbs-save" onClick={save} className="bg-primary hover:bg-primary/90">
            {editing ? "Save changes" : "Add milestone"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
